import React, { useState, useEffect } from 'react';
import {
  ScrollRestoration, Outlet, useNavigate, NavigateFunction, useLocation,
} from 'react-router-dom';
import { useSelector } from 'react-redux';
import Container from 'react-bootstrap/Container';

import Header from 'components/NewHome/Header';
import Sidebar from 'components/NewHome/Sidebar';
import MobileTopBar from 'components/Layout/MobileShell/MobileTopBar';
import BottomTabBar from 'components/Layout/MobileShell/BottomTabBar';
import MoreSheet from 'components/Layout/MobileShell/MoreSheet';
import useIsMobile from 'utils/hooks/useIsMobile';
import { selectAuthToken } from 'redux/reducers/authReducer';
import SubmissionProgressIndicator from 'components/SubmissionProgressIndicator';
import DownloadProgressCard from 'components/DownloadProgressCard';
import useSubmissionStatus from 'utils/hooks/financial/useSubmissionStatus';
import logout from 'utils/logout';
import { store } from 'redux/store';
import { setInfiniteScrollBottomState } from 'redux/reducers/infiniteScrollReducer';
import useTokenExpirationCheck from 'utils/hooks/useTokenExpirationCheck';

import './index.scss';

// Rotas que renderizam seu próprio MobileSectionHeader (voltar + título de seção)
// em vez da MobileTopBar global — evita duplicar cabeçalho no mobile.
const OWN_MOBILE_HEADER_PATHS = [
  '/meteorology/fields',
  '/meteorology/weather',
  '/agro',
  '/meteorology/videos',
  '/meteorology/tutorial',
  '/flux/videos',
  '/flux/upload',
  '/flux/personalized',
  '/flux/automatic',
  '/flux/about',
  '/windx/automatic',
  // Com a barra final: a home `/relatorios` mantém a MobileTopBar global.
  '/relatorios/',
  '/admin/users',
  '/admin/companies',
  '/admin/financial',
  '/admin/mailer',
  '/admin/flux/about',
  '/admin/windx/models',
];

export function defaultLayoutOnScroll(event: React.UIEvent<HTMLElement>) {
  const div = event.target as HTMLDivElement;
  const bottom = Math.abs(div.scrollHeight - div.scrollTop - div.clientHeight) < 2;
  store.dispatch(setInfiniteScrollBottomState(bottom));
}

export function checkAccessToken(
  navigate: NavigateFunction,
  setAccessTokenChecked: (value: boolean) => void,
  path: string,
  accessToken?: string,
): void {
  if (!accessToken) {
    logout(
      navigate,
      {
        title: '',
        message: 'Você precisa estar logado para acessar esta página.',
        variant: 'warning',
      },
      path,
    );
  }
  setAccessTokenChecked(true);
}

export default function MainLayout() {
  const navigate = useNavigate();
  const accessToken = useSelector(selectAuthToken);
  const location = useLocation();
  const isMobile = useIsMobile();
  const [moreOpen, setMoreOpen] = useState<boolean>(false);
  useTokenExpirationCheck(navigate);
  const [accessTokenChecked, setAccessTokenChecked] = useState<boolean>(false);

  useEffect(() => {
    if (!accessTokenChecked) {
      checkAccessToken(navigate, setAccessTokenChecked, location.pathname, accessToken);
    }
  }, [navigate, accessTokenChecked, accessToken, location]);

  const { isProcessing, progress } = useSubmissionStatus();
  const hasOwnMobileHeader = OWN_MOBILE_HEADER_PATHS.some(
    (path) => location.pathname.startsWith(path),
  );
  const showMobileTopBar = isMobile && !hasOwnMobileHeader;

  return (
    <Container fluid className="h-100 p-0 d-flex flex-column">
      {showMobileTopBar && <MobileTopBar onAvatarClick={() => setMoreOpen(true)} />}
      {!isMobile && <Header />}
      <div className="d-flex flex-row flex-grow-1 overflow-hidden">
        {!isMobile && <Sidebar />}
        <div
          className={`new-home-outlet-container${isMobile ? ' is-mobile' : ''}`}
          onScroll={defaultLayoutOnScroll}
        >
          <Outlet />
          <SubmissionProgressIndicator isVisible={isProcessing} progress={progress} />
          <DownloadProgressCard />
        </div>
        <ScrollRestoration />
      </div>
      {isMobile && <BottomTabBar onMoreClick={() => setMoreOpen(true)} />}
      {isMobile && <MoreSheet open={moreOpen} dismiss={() => setMoreOpen(false)} />}
    </Container>
  );
}
